import { IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '../../types.ts'
import { useFamilyAxes } from '../../hooks/useFamilyAxes.ts'
import { LEGACY_AXIS } from '../../utils/familyAxes.ts'
import { hiddenValueClass } from '../../utils/hiddenClass.ts'
import style from './VariantSelect.module.scss'

interface IVariantSelectProps {
  schema: IEcommerceQuestionSchema
  answer: IEcommerceAnswerSchema
  onSelect: (index: number) => void
}

// Buy-box variant picker: one row of brand pill buttons per axis. Lower axes filter out values
// incompatible with the selections above (filter, not disable — the family convention); a preview-hidden
// value renders regardless, washed out and inert. Spec §Multi-Axis Selector Pattern.
const VariantSelect = ({ schema, answer, onSelect }: IVariantSelectProps) => {
  const { childrenProducts, axes, currentSelections, findSKUForValue, isCompatibleWithUpperAxes, isHiddenInPreview } =
    useFamilyAxes({
      answer,
      schema
    })

  if (childrenProducts.length <= 1 || 0 === axes.length) return null

  const onChange = (axisName: string, value: string) => {
    const targetSKU = findSKUForValue(axisName, value)
    if (targetSKU && targetSKU.id !== answer.id) onSelect(targetSKU.index)
  }

  return (
    <div className={style.variants} data-testid="SW-EC-VariantSelect">
      {axes.map((axis, axisIndex) => {
        const selectedValue = currentSelections[axis.name] || ''
        return (
          <div key={axis.name}>
            {axis.name !== LEGACY_AXIS && (
              <div className={style.label}>
                <span className={style.labelName}>{axis.name}:</span> <span>{selectedValue}</span>
              </div>
            )}
            <div className={style.values}>
              {axis.values
                .filter(value => isHiddenInPreview(axis.name, value) || isCompatibleWithUpperAxes(axisIndex, value))
                .map(value => (
                  <button
                    type="button"
                    key={value}
                    aria-pressed={value === selectedValue}
                    className={`${style.value} ${value === selectedValue ? style.valueSelected : ''} ${hiddenValueClass(
                      isHiddenInPreview(axis.name, value)
                    )}`}
                    onClick={() => onChange(axis.name, value)}
                  >
                    {value}
                  </button>
                ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default VariantSelect
